import { Box, ProgressBar, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import React from "react";

import {
  QUIZ_GENERATION_PROGRESS_CAP,
  QUIZ_GENERATION_TOTAL_STEPS,
} from "./consts";

interface QuizWriterGeneratingCardProps {
  topicDisplayName: string;
  generationLogStepCount: number;
  latestUserLogMessage: string | null;
  didYouKnowFact: string;
}

export function QuizWriterGeneratingCard({
  topicDisplayName,
  generationLogStepCount,
  latestUserLogMessage,
  didYouKnowFact,
}: QuizWriterGeneratingCardProps) {
  const { t } = useTranslation("learnerApp\\Quiz");

  const currentStep = Math.min(
    generationLogStepCount,
    QUIZ_GENERATION_PROGRESS_CAP,
  );

  return (
    <Box
      sx={{
        backgroundColor: "common.white",
        borderRadius: "20px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        border: "1px solid",
        borderColor: "grey.100",
        overflow: "hidden",
      }}
    >
      <Box sx={{ padding: { xs: "20px", sm: "28px" } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            marginBottom: "4px",
          }}
        >
          {t("Quiz Writer")}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", fontSize: "1.5rem" }}
        >
          <Trans
            t={t}
            values={{
              topicDisplayName: topicDisplayName.trim() || t("your topic"),
            }}
          >
            Creating a quiz about{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {{ topicDisplayName: topicDisplayName.trim() || t("your topic") }}
            </Box>
          </Trans>
        </Typography>
      </Box>
      <Box sx={{ height: "1px", backgroundColor: "grey.200", opacity: 0.6 }} />
      <Box
        sx={{
          padding: { xs: "24px", sm: "32px" },
          textAlign: "center",
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: "2rem",
            lineHeight: 1,
            marginBottom: "16px",
          }}
          aria-hidden
        >
          ✨
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            marginBottom: "20px",
            fontSize: "1.6rem",
          }}
        >
          {t("Preparing your quiz...")}
        </Typography>
        <ProgressBar
          variant="determinate"
          value={(currentStep / QUIZ_GENERATION_TOTAL_STEPS) * 100}
          sx={{
            height: 10,
            borderRadius: 999,
            maxWidth: 440,
            margin: "0 auto",
            backgroundColor: "#4CAF50",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              backgroundColor: "#4CAF50",
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            fontSize: "1.3rem",
          }}
        >
          <Box component="span" sx={{ color: "success.main" }}>
            ✓
          </Box>{" "}
          {t("{{current}} of {{total}} steps done", {
            current: currentStep,
            total: QUIZ_GENERATION_TOTAL_STEPS,
          })}
        </Typography>
        <Box
          sx={{
            marginTop: "20px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "8px",
            paddingX: { xs: 0, sm: "16px" },
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: "1.25rem", lineHeight: 1.4 }}
            aria-hidden
          >
            🔍
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              textAlign: "left",
              flex: 1,
              maxWidth: 400,
              fontSize: "1.5rem",
            }}
          >
            {latestUserLogMessage ?? t("Starting up…")}
            <Box
              component="span"
              sx={{
                marginLeft: "4px",
                color: "grey.400",
                letterSpacing: 3,
                "@keyframes quizWriterPulse": {
                  "0%, 100%": { opacity: 0.35 },
                  "50%": { opacity: 1 },
                },
                animation: "quizWriterPulse 1.2s ease-in-out infinite",
              }}
            >
              ···
            </Box>
          </Typography>
        </Box>
      </Box>
      <Box sx={{ padding: { xs: "20px", sm: "24px" }, paddingTop: 0 }}>
        <Box
          sx={{
            borderRadius: "14px",
            backgroundColor: "#FFFBEB",
            padding: "16px",
            textAlign: "left",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#92400E",
              letterSpacing: "0.06em",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "8px",
              fontSize: "1.2rem",
            }}
          >
            <span aria-hidden>💡</span> {t("DID YOU KNOW?")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#1C1917",
              lineHeight: 1.55,
              fontSize: "1.4rem",
            }}
          >
            {didYouKnowFact}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
